// Painel "Pendências do Mercado Livre" na página do canal (adapter ml).
// Mostra o que a publicação automática já colocou no ar, o que está na fila e,
// por SKU, cada motivo pelo qual ainda não entrou. Atualiza a cada 60 s e ao focar a aba.
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { AlertCircle, CheckCircle2, Search as SearchIcon } from "lucide-react";
import { api, apiErrorMessage } from "../../lib/api";
import {
  filterPendencies,
  reasonChips,
  reasonLabel,
  type MlPendencies,
  type MlPendencyItem,
} from "../../lib/ml-channel";
import { relativeTime } from "../../lib/utils";
import { Badge } from "../ui/badge";
import { Input } from "../ui/input";
import { Skeleton } from "../ui/skeleton";

const REFRESH_MS = 60_000;

export function MlPendenciesPanel({ slug }: { slug: string }): JSX.Element {
  const [group, setGroup] = useState<string | null>(null);
  const [search, setSearch] = useState("");

  const q = useQuery<MlPendencies>({
    queryKey: ["ml-pendencies", slug],
    queryFn: async () => (await api.get<MlPendencies>(`/api/channels/${slug}/ml/pendencies`)).data,
    refetchInterval: REFRESH_MS,
    refetchOnWindowFocus: true,
    enabled: slug.length > 0,
  });

  const items = useMemo(() => q.data?.items ?? [], [q.data]);
  const chips = useMemo(() => reasonChips(items), [items]);
  const shown = useMemo(() => filterPendencies(items, group, search), [items, group, search]);
  const filtering = group !== null || search.trim() !== "";

  return (
    <section className="card" aria-labelledby="ml-pend-title" style={{ padding: 0 }}>
      <div className="stack" style={{ gap: ".5rem", padding: "0.75rem 1rem", borderBottom: "0.0625rem solid var(--color-line)" }}>
        <div className="spread">
          <h2 id="ml-pend-title" style={{ margin: 0, fontSize: "var(--text-xl)", fontWeight: 900 }}>
            Pendências do Mercado Livre
          </h2>
          {q.data ? (
            <span className="card__meta">
              {q.data.last_run_at ? `última rodada ${relativeTime(q.data.last_run_at)}` : "ainda sem rodada"}
              {q.data.enabled ? "" : " · publicação automática desligada"}
            </span>
          ) : null}
        </div>
        <p className="field__hint" style={{ margin: 0 }}>
          Entra sozinho todo SKU ativo com estoque no varejo e foto; o que falta aparece aqui.
        </p>
        {q.data ? (
          <ul className="cluster" style={{ margin: 0, padding: 0, listStyle: "none" }} aria-label="Resumo">
            <li><Badge variant="live">{q.data.summary.live} no ar</Badge></li>
            <li><Badge variant="draft">{q.data.summary.queued} na fila</Badge></li>
            <li><Badge variant="warning">{q.data.summary.pending} com pendência</Badge></li>
            <li><Badge variant="outline">{q.data.summary.excluded} fora da regra</Badge></li>
          </ul>
        ) : null}
      </div>

      {q.isLoading ? (
        <div className="stack" style={{ padding: "0.75rem 1rem" }} aria-busy="true">
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} className="h-14 w-full" />
          ))}
        </div>
      ) : q.isError || !q.data ? (
        <p
          className="field__hint"
          role="alert"
          style={{ margin: 0, padding: "0.75rem 1rem", color: "var(--color-warn)", display: "flex", gap: ".5rem", alignItems: "center" }}
        >
          <AlertCircle className="icon" aria-hidden />
          <span>Não deu para ler as pendências ({apiErrorMessage(q.error)}).</span>
        </p>
      ) : items.length === 0 ? (
        <p
          className="field__hint"
          role="status"
          style={{ margin: 0, padding: "0.75rem 1rem", display: "flex", gap: ".5rem", alignItems: "center" }}
        >
          <CheckCircle2 className="icon" aria-hidden style={{ color: "var(--color-good)" }} />
          <span>Nenhuma pendência: tudo que cumpre a regra já está no ar ou na fila.</span>
        </p>
      ) : (
        <>
          <div className="stack" style={{ gap: ".5rem", padding: "0.75rem 1rem", borderBottom: "0.0625rem solid var(--color-line)" }}>
            <div className="relative">
              <SearchIcon
                className="icon"
                aria-hidden
                style={{ position: "absolute", left: "0.75rem", top: "50%", transform: "translateY(-50%)", color: "var(--ink-3)" }}
              />
              <Input
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Buscar SKU ou nome..."
                aria-label="Buscar pendências por SKU ou nome"
                style={{ paddingLeft: "2.25rem" }}
              />
            </div>
            <div className="cluster" role="group" aria-label="Filtrar por motivo">
              <FilterChip active={group === null} onClick={() => setGroup(null)} label={`Todos (${items.length})`} />
              {chips.map((c) => (
                <FilterChip
                  key={c.group}
                  active={group === c.group}
                  onClick={() => setGroup(group === c.group ? null : c.group)}
                  label={`${c.label} (${c.count})`}
                />
              ))}
            </div>
          </div>

          {shown.length === 0 ? (
            <p className="field__hint" role="status" style={{ margin: 0, padding: "0.75rem 1rem" }}>
              Nenhum SKU bate com {filtering ? "esse filtro" : "a busca"}.
            </p>
          ) : (
            <ul style={{ margin: 0, padding: 0, listStyle: "none" }} aria-label={`${shown.length} SKUs com pendência`}>
              {shown.map((it) => (
                <PendencyRow key={it.sku} slug={slug} item={it} />
              ))}
            </ul>
          )}
        </>
      )}
    </section>
  );
}

function FilterChip({ active, onClick, label }: { active: boolean; onClick: () => void; label: string }): JSX.Element {
  return (
    <button
      type="button"
      className="chip"
      aria-pressed={active}
      onClick={onClick}
      style={{
        cursor: "pointer",
        border: "0.0625rem solid var(--color-line)",
        background: active ? "var(--color-coral-soft)" : "transparent",
        color: active ? "var(--color-coral)" : "var(--color-ink)",
        minHeight: "1.75rem",
      }}
    >
      {label}
    </button>
  );
}

function PendencyRow({ slug, item }: { slug: string; item: MlPendencyItem }): JSX.Element {
  return (
    <li
      className="stack"
      style={{ gap: ".375rem", padding: "0.625rem 1rem", borderTop: "0.0625rem solid var(--color-line)" }}
    >
      <div className="spread" style={{ flexWrap: "nowrap", alignItems: "baseline" }}>
        <div className="cluster" style={{ flexWrap: "nowrap", minWidth: 0, alignItems: "baseline" }}>
          {item.has_publication ? (
            <Link
              to={`/canais/${slug}/produtos/${encodeURIComponent(item.sku)}`}
              className="mono"
              style={{ fontSize: "var(--fs-micro)", fontWeight: 700, flexShrink: 0 }}
              aria-label={`Abrir ficha do SKU ${item.sku}`}
            >
              {item.sku}
            </Link>
          ) : (
            <span className="mono muted" style={{ fontSize: "var(--fs-micro)", flexShrink: 0 }}>
              {item.sku}
            </span>
          )}
          <span className="truncate" style={{ fontWeight: "var(--fw-black)", minWidth: 0 }}>
            {item.name || "(sem nome)"}
          </span>
        </div>
        <div className="cluster" style={{ flexWrap: "nowrap", flexShrink: 0 }}>
          <Badge variant={item.state === "queued" ? "draft" : "warning"}>
            {item.state === "queued" ? "Na fila" : "Pendente"}
          </Badge>
          <span className="field__hint" style={{ whiteSpace: "nowrap" }}>
            estoque {item.stock}
          </span>
        </div>
      </div>

      {item.reasons.length > 0 ? (
        <ul style={{ margin: 0, padding: 0, listStyle: "none" }} className="stack">
          {item.reasons.map((r, i) => (
            <li key={`${r.code}-${i}`} className="cluster" style={{ alignItems: "baseline", gap: ".5rem", flexWrap: "nowrap" }}>
              <Badge variant="warning" style={{ flexShrink: 0 }}>{reasonLabel(r.code)}</Badge>
              <span style={{ fontSize: "var(--text-base)", minWidth: 0 }}>{r.text}</span>
            </li>
          ))}
        </ul>
      ) : (
        <p className="field__hint" style={{ margin: 0 }}>
          Sem motivo registrado: entra na próxima rodada.
        </p>
      )}

      <div className="field__hint">
        {item.has_publication ? (
          <Link to={`/canais/${slug}/produtos/${encodeURIComponent(item.sku)}`} style={{ fontWeight: 700 }}>
            Abrir ficha
          </Link>
        ) : (
          <span>ficha ainda não criada</span>
        )}
        {item.evaluated_at ? ` · avaliado ${relativeTime(item.evaluated_at)}` : ""}
      </div>
    </li>
  );
}
